import { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useTheme, alpha } from '@mui/material/styles';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';

// SVG dimensions for the new body diagram
// eslint-disable-next-line no-unused-vars
const SVG_WIDTH = 177; // Half of 354 for each body (front/back)
const SVG_HEIGHT = 475;

// Map body part names to translation keys for i18n
const BODY_PART_TRANSLATION_KEYS = {
  Head: 'form.injuryReport.head',
  Neck: 'form.injuryReport.neck',
  Chest: 'form.injuryReport.chest',
  Abdomen: 'form.injuryReport.abdomen',
  'Left Ear': 'form.injuryReport.leftEar',
  'Right Ear': 'form.injuryReport.rightEar',
  'Left Shoulder': 'form.injuryReport.leftShoulder',
  'Right Shoulder': 'form.injuryReport.rightShoulder',
  'Left Upper Arm': 'form.injuryReport.leftUpperArm',
  'Right Upper Arm': 'form.injuryReport.rightUpperArm',
  'Left Elbow': 'form.injuryReport.leftElbow',
  'Right Elbow': 'form.injuryReport.rightElbow',
  'Left Forearm': 'form.injuryReport.leftForearm',
  'Right Forearm': 'form.injuryReport.rightForearm',
  'Left Hip': 'form.injuryReport.leftHip',
  'Right Hip': 'form.injuryReport.rightHip',
  'Back of Head': 'form.injuryReport.backOfHead',
  'Back of Neck': 'form.injuryReport.backOfNeck',
  'Upper Back': 'form.injuryReport.upperBack',
  'Lower Back': 'form.injuryReport.lowerBack',
  Groin: 'form.injuryReport.groin',
  'Left Back Upper Arm': 'form.injuryReport.leftBackUpperArm',
  'Right Back Upper Arm': 'form.injuryReport.rightBackUpperArm',
  'Left Back Elbow': 'form.injuryReport.leftBackElbow',
  'Right Back Elbow': 'form.injuryReport.rightBackElbow',
  'Left Back Forearm': 'form.injuryReport.leftBackForearm',
  'Right Back Forearm': 'form.injuryReport.rightBackForearm',
  'Left Back Hip': 'form.injuryReport.leftBackHip',
  'Right Back Hip': 'form.injuryReport.rightBackHip'
};

// Function to determine body part name from coordinates for front view (left figure in SVG).
// Regions are derived directly from the traced SVG silhouette (viewBox 0 0 160 475,
// body centred at x≈80). We use gap-free vertical bands and split each band left/right,
// so every point on the figure resolves to exactly one part with no overlaps or holes.
// Labelling matches the click side (NOT mirrored): screen-left (small x) → "Left ..." —
// consistent with the back view, so the label always matches the side the user clicks.
const getBodyPartNameFront = (x, y) => {
  // Head & ears (chin ends at y≈59; ears sit on the sides of the head at y≈26–48)
  if (y <= 57) {
    if (y >= 26 && y <= 48 && x <= 58) return 'Left Ear';
    if (y >= 26 && y <= 48 && x >= 102) return 'Right Ear';
    return 'Head';
  }

  // Neck (narrow centre column, y≈57–88 down to the collar line) / very top of shoulders
  if (y <= 88) {
    if (x < 60) return 'Left Shoulder';
    if (x > 100) return 'Right Shoulder';
    return 'Neck';
  }

  // Shoulders / upper chest
  if (y <= 108) {
    if (x < 50) return 'Left Shoulder';
    if (x > 110) return 'Right Shoulder';
    return 'Chest';
  }

  // Chest / upper arms
  if (y <= 150) {
    if (x < 42) return 'Left Upper Arm';
    if (x > 118) return 'Right Upper Arm';
    return 'Chest';
  }

  // Upper abdomen / lower upper-arm
  if (y <= 165) {
    if (x < 40) return 'Left Upper Arm';
    if (x > 120) return 'Right Upper Arm';
    return 'Abdomen';
  }

  // Abdomen / elbows (the arm's mid-joint sits at y≈165–190)
  if (y <= 190) {
    if (x < 38) return 'Left Elbow';
    if (x > 122) return 'Right Elbow';
    return 'Abdomen';
  }

  // Abdomen / forearms
  if (y <= 232) {
    if (x < 36) return 'Left Forearm';
    if (x > 124) return 'Right Forearm';
    return 'Abdomen';
  }

  // Pelvis band: hips (sides) + groin (whole centre column, incl. the crotch at y≈248) /
  // forearm-to-hand (outer)
  if (y <= 290) {
    if (x < 32) return y <= 255 ? 'Left Forearm' : 'Left Hand';
    if (x > 128) return y <= 255 ? 'Right Forearm' : 'Right Hand';
    if (x < 60) return 'Left Hip';
    if (x > 100) return 'Right Hip';
    return 'Groin';
  }

  // Thighs
  if (y <= 338) return x < 80 ? 'Left Thigh' : 'Right Thigh';

  // Knees (the knee joint sits at y≈345–392, roughly the mid-point of the leg)
  if (y <= 392) return x < 80 ? 'Left Knee' : 'Right Knee';

  // Shins / lower legs
  if (y <= 458) return x < 80 ? 'Left Shin' : 'Right Shin';

  // Feet
  return x < 80 ? 'Left Foot' : 'Right Foot';
};

// Function to determine body part name from coordinates for back view (right figure in SVG).
// Same traced silhouette and gap-free banding as the front view. The back view is NOT
// mirrored, so screen-left (small x) maps to the person's LEFT side. Coordinates are
// relative to the back figure (0-160); the +195 SVG offset is applied only when rendering.
const getBodyPartNameBack = (x, y) => {
  // Head & ears (base of skull ends at y≈59; ears on the sides at y≈26–48)
  if (y <= 57) {
    if (y >= 26 && y <= 48 && x <= 58) return 'Left Ear';
    if (y >= 26 && y <= 48 && x >= 102) return 'Right Ear';
    return 'Back of Head';
  }

  // Neck (narrow centre column, y≈57–88 down to the collar line) / very top of shoulders
  if (y <= 88) {
    if (x < 60) return 'Left Shoulder';
    if (x > 100) return 'Right Shoulder';
    return 'Back of Neck';
  }

  // Shoulders / upper back
  if (y <= 108) {
    if (x < 50) return 'Left Shoulder';
    if (x > 110) return 'Right Shoulder';
    return 'Upper Back';
  }

  // Upper back / upper arms
  if (y <= 165) {
    if (x < 42) return 'Left Back Upper Arm';
    if (x > 118) return 'Right Back Upper Arm';
    return 'Upper Back';
  }

  // Lower back / elbows
  if (y <= 190) {
    if (x < 38) return 'Left Back Elbow';
    if (x > 122) return 'Right Back Elbow';
    return 'Lower Back';
  }

  // Lower back / forearms
  if (y <= 232) {
    if (x < 36) return 'Left Back Forearm';
    if (x > 124) return 'Right Back Forearm';
    return 'Lower Back';
  }

  // Pelvis band: hips (sides) + buttocks (centre-low) / forearm-to-hand (outer)
  if (y <= 290) {
    if (x < 32) return y <= 255 ? 'Left Back Forearm' : 'Left Back Hand';
    if (x > 128) return y <= 255 ? 'Right Back Forearm' : 'Right Back Hand';
    if (x < 60) return 'Left Back Hip';
    if (x > 100) return 'Right Back Hip';
    return y <= 255 ? 'Lower Back' : 'Buttocks';
  }

  // Thighs
  if (y <= 338) return x < 80 ? 'Left Back Thigh' : 'Right Back Thigh';

  // Knees (the knee joint sits at y≈345–392, roughly the mid-point of the leg)
  if (y <= 392) return x < 80 ? 'Left Back Knee' : 'Right Back Knee';

  // Calves
  if (y <= 458) return x < 80 ? 'Left Calf' : 'Right Calf';

  // Feet
  return x < 80 ? 'Left Back Foot' : 'Right Back Foot';
};

// Combined human body SVG that contains both front and back views
// The viewBox will be used to show only the relevant portion
const BodyOutline = () => (
  <g fill="none" stroke="#333" strokeWidth="1">
    {/* FRONT VIEW (left side of SVG) */}
    {/* Front head outline - fill style matching body outline */}
    <path
      d="M79.5 0C91.19 0 100.884 11.2597 102.694 26.0059C105.642 26.1362 108 29.2168 108 33C108 36.866 105.538 40 102.5 40C102.334 40 102.171 39.9889 102.009 39.9707C100.19 48.0394 95.992 54.8338 90.5 58.7881V57.5352C95.467 53.7054 99.315 47.3741 101.033 39.751L101.23 38.877L102.121 38.9766C102.253 38.9914 102.379 39 102.5 39C104.774 39 107 36.5518 107 33C107 29.5317 104.868 27.1031 102.65 27.0049L101.805 26.9678L101.702 26.1279C99.925 11.6505 90.481 1 79.5 1C68.519 1 59.074 11.6505 57.297 26.1279L57.194 26.9678L56.349 27.0049C54.132 27.1037 52 29.5322 52 33C52 36.5518 54.226 39 56.5 39C56.621 39 56.746 38.9915 56.879 38.9766L57.769 38.877L57.966 39.751C59.685 47.3745 63.533 53.7064 68.5 57.5361V58.7881C63.008 54.8339 58.809 48.0396 56.99 39.9707C56.829 39.9889 56.665 40 56.5 40C53.462 40 51 36.866 51 33C51 29.2172 53.358 26.1368 56.305 26.0059C58.115 11.2596 67.81 0 79.5 0Z"
      fill="black"
    />

    {/* Front body outline */}
    <path
      d="M68.5 68.3008L42.7344 81.9414L42.6719 81.9746L42.6025 81.9893L33.1777 83.9727L25.3262 88.3887L20.9229 93.7715L17 101.618V119.577L16.9766 119.651L13.4814 130.634L10.5 143.059V152.038L8.48828 165.114L8.47656 165.15L5.4834 174.626L3 186.55V231.523L2.99805 231.548L1 252.527V260.896L4.41992 268.713L11.207 275.5H14V272.626L10.5596 266.237L10.4873 266.104L12.002 250.452L12.9912 250.406L14.9658 260.775L17.4639 263.689L19.5 260.839V245.13L15.0645 237.245L15 237.131V231.432L15.0186 231.365L18.5186 218.865L21.0127 209.884L23.0059 200.42L24 190.479V176.062L23.0146 172.121L22.9551 171.884L23.1064 171.691L28.5498 164.763L34.5 145.425V136.967L34.5039 136.934L35.5 129.463V119.5L36.4951 119.43L38.9902 136.897L42.9834 151.871L42.9854 151.879L42.9873 151.888L45.9873 164.888L45.9902 164.899L47.9922 175.91L48 175.955V190.532L47.9961 190.564L45.9961 206.064L45.9912 206.102L45.9805 206.138L41.9873 220.113L39.9922 231.586L39.9912 231.594L37.9912 242.094L36.5 250.542V283.963L38.4941 298.921L40.4941 311.421L40.5 311.46V330.54L40.4941 330.579L38.4941 343.079L38.4922 343.089L38.4902 343.098L35.9902 355.598L34.5 364.541V380.965L35.9941 390.926L35.9961 390.936L37.4941 402.424L39.4912 412.906L39.4941 412.921L39.4961 412.936L40.9961 424.436L41 424.468V446.557L40.9873 446.612L39.4873 453.112L39.4678 453.196L39.4219 453.269L35.9219 458.769L35.9092 458.788L35.8945 458.807L32.4111 463.284L29.0498 468.567L30.376 472.548L37.0361 473.5H53.3623L58 470.717V455.59L56.5322 451.676L56.4785 451.534L56.5127 451.388L57.9785 445.033L56.5322 441.176L56.5 441.091V416.956L56.5078 416.913L58.0078 408.413L58.0117 408.388L58.0195 408.362L61.0127 397.886L62.5107 390.896L64.5 379.459V346.891L64.5459 346.79L67.5068 340.373L68.5 325.979V314.938L68.5146 314.879L71.0146 304.879L71.0176 304.868L74.0068 293.905L75.5 279.974V248H83.5V279.974L84.9922 293.905L87.9824 304.868L87.9854 304.879L90.4854 314.879L90.5 314.938V325.979L91.4922 340.373L94.4541 346.79L94.5 346.891V379.459L96.4893 390.896L97.9863 397.886L100.98 408.362L100.988 408.388L100.992 408.413L102.492 416.913L102.5 416.956V441.091L102.468 441.176L101.021 445.033L102.487 451.388L102.521 451.534L102.468 451.676L101 455.59V470.717L105.638 473.5H121.964L128.623 472.548L129.949 468.567L126.588 463.284L123.105 458.807L123.091 458.788L123.078 458.769L119.578 453.269L119.532 453.196L119.513 453.112L118.013 446.612L118 446.557V424.468L118.004 424.436L119.504 412.936L119.506 412.921L119.509 412.906L121.505 402.424L123.004 390.936L123.006 390.926L124.5 380.965V364.541L123.01 355.598L120.51 343.098L120.508 343.089L120.506 343.079L118.506 330.579L118.5 330.54V311.46L118.506 311.421L120.506 298.921L122.5 283.963V250.542L121.009 242.094L119.009 231.594L119.008 231.586L117.012 220.113L113.02 206.138L113.009 206.102L113.004 206.064L111.004 190.564L111 190.532V175.955L111.008 175.91L113.01 164.899L113.013 164.888L116.013 151.888L116.015 151.879L116.017 151.871L120.009 136.897L122.505 119.43L123.5 119.5V129.463L124.496 136.934L124.5 136.967V145.425L130.449 164.763L135.894 171.691L136.045 171.884L135.985 172.121L135 176.062V190.479L135.993 200.42L137.986 209.884L140.481 218.865L143.981 231.365L144 231.432V237.131L143.936 237.245L139.5 245.13V260.839L141.536 263.689L144.033 260.775L146.009 250.406L146.998 250.452L148.513 266.104L148.44 266.237L145 272.626V275.5H147.793L154.579 268.713L158 260.896V252.527L156.002 231.548L156 231.523V186.55L153.516 174.626L150.523 165.15L150.512 165.114L148.5 152.038V143.059L145.518 130.634L142.023 119.651L142 119.577V101.618L138.076 93.7715L133.673 88.3887L125.821 83.9727L116.397 81.9893L116.328 81.9746L116.266 81.9414L90.5 68.3008V58H91.5V67.6992L116.671 81.0244L126.103 83.0107L126.178 83.0264L126.245 83.0645L134.245 87.5645L134.327 87.6104L134.387 87.6836L138.922 93.2266L138.947 93.2764L143 101.382V119.422L146.477 130.349L146.482 130.365L146.486 130.384L149.486 142.884L149.5 142.94V151.962L151.488 164.887L154.477 174.35L154.484 174.373L154.489 174.398L156.989 186.398L157 186.448V231.473L158.998 252.452L159 252.477V261.104L158.958 261.2L155.458 269.2L155.42 269.287L148.207 276.5H144V272.374L144.06 272.263L147.487 265.896L146.338 254.021L144.991 261.094L144.966 261.225L144.88 261.325L141.464 265.311L138.593 261.291L138.5 261.16V244.869L138.564 244.755L143 236.869V231.568L139.519 219.135V219.134L137.019 210.134L137.014 210.118L137.011 210.103L135.011 200.603L135.005 200.576L135.003 200.55L134.003 190.55L134 190.525V175.938L134.015 175.879L134.955 172.116L129.606 165.309L129.55 165.236L123.522 145.647L123.5 145.575V137.037L122.504 129.566L122.5 129.533V126.536L120.995 137.07L120.991 137.101L120.983 137.129L116.983 152.129L113.992 165.09L112 176.047V190.466L113.991 205.901L117.98 219.862L117.988 219.888L117.992 219.914L119.991 231.406L121.991 241.906L121.992 241.913L123.492 250.413L123.5 250.456V284.033L123.496 284.066L121.496 299.066L121.494 299.079L119.5 311.542V330.457L121.494 342.921L123.99 355.402L123.992 355.41L123.993 355.418L125.5 364.459V381.037L125.494 381.074L123.995 391.063L123.996 391.064L122.496 402.564L122.494 402.579L122.491 402.594L120.494 413.075L119 424.534V446.442L120.468 452.804L123.911 458.215L127.395 462.693L127.409 462.712L127.422 462.731L130.922 468.231L131.05 468.433L129.377 473.451L129.07 473.495L122.07 474.495L122.035 474.5H105.361L105.243 474.429L100.243 471.429L100 471.283V455.409L100.032 455.324L101.479 451.466L100.013 445.112L99.9785 444.966L100.032 444.824L101.5 440.91V417.042L100.012 408.612L97.0195 398.138L97.0146 398.121L97.0107 398.104L95.5107 391.104L95.5088 391.096L95.5078 391.086L93.5078 379.586L93.5 379.543V347.11L90.5459 340.71L90.5078 340.626L90.501 340.534L89.501 326.034L89.5 326.018V315.062L87.0176 305.132L84.0176 294.132L84.0068 294.093L84.0029 294.054L82.5 280.026V249H76.5V280.026L74.9971 294.054L74.9932 294.093L74.9824 294.132L71.9824 305.132L69.5 315.062V326.018L69.499 326.034L68.499 340.534L68.4922 340.626L68.4541 340.71L65.5 347.11V379.543L65.4922 379.586L63.4922 391.086L63.4912 391.096L63.4893 391.104L61.9893 398.104L61.9854 398.121L61.9805 398.138L58.9873 408.612L57.5 417.042V440.91L58.9678 444.824L59.0215 444.966L58.9873 445.112L57.5205 451.466L58.9678 455.324L59 455.409V471.283L58.7568 471.429L53.7568 474.429L53.6387 474.5H36.9648L36.9297 474.495L29.9297 473.495L29.623 473.451L27.9502 468.433L28.0781 468.231L31.5781 462.731L31.5908 462.712L31.6055 462.693L35.0879 458.215L38.5312 452.804L40 446.442V424.534L38.5049 413.075L36.5088 402.594L36.5059 402.579L36.5039 402.564L35.0039 391.064V391.063L33.5059 381.074L33.5 381.037V364.459L35.0068 355.418L35.0078 355.41L35.0098 355.402L37.5059 342.921L39.5 330.457V311.542L37.5059 299.079L37.5039 299.066L35.5039 284.066L35.5 284.033V250.456L35.5078 250.413L37.0078 241.913L37.0088 241.906L39.0088 231.406L41.0078 219.914L41.0117 219.888L41.0195 219.862L45.0078 205.901L47 190.466V176.047L45.0078 165.09H45.0068L42.0166 152.129L38.0166 137.129L38.0088 137.101L38.0049 137.07L36.5 126.536V129.533L36.4961 129.566L35.5 137.037V145.575L35.4775 145.647L29.4502 165.236L29.3936 165.309L24.0439 172.116L24.9854 175.879L25 175.938V190.525L24.9971 190.55L23.9971 200.55L23.9951 200.576L23.9893 200.603L21.9893 210.103L21.9863 210.118L21.9814 210.134L19.4814 219.134V219.135L16 231.568V236.869L20.4355 244.755L20.5 244.869V261.16L20.4072 261.291L17.5361 265.311L14.1201 261.325L14.0342 261.225L14.0088 261.094L12.6611 254.021L11.5117 265.896L14.9404 272.263L15 272.374V276.5H10.793L3.58008 269.287L3.54199 269.2L0.0419922 261.2L0 261.104V252.477L0.00195312 252.452L2 231.473V186.448L2.01074 186.398L4.51074 174.398L4.51562 174.373L4.52344 174.35L7.51074 164.887L9.5 151.962V142.94L9.51367 142.884L12.5137 130.384L12.5176 130.365L12.5234 130.349L16 119.422V101.382L20.0527 93.2764L20.0781 93.2266L24.6133 87.6836L24.6729 87.6104L24.7549 87.5645L32.7549 83.0645L32.8223 83.0264L32.8975 83.0107L42.3281 81.0244L67.5 67.6992V58H68.5V68.3008Z"
      fill="black"
    />

    {/* Front neck/collar lines - diagonal lines from reference SVG */}
    <path d="M108.797 86.5049L98.1903 86.529L90.2066 89.4462" stroke="black" />
    <path d="M51 86.5049L61.6066 86.529L69.5903 89.4462" stroke="black" />

    {/* Front body center line - chest mark */}
    <path d="M81 201V206" stroke="black" />

    {/* BACK VIEW (right side of SVG) */}
    {/* Back head outline - no facial features */}
    <path
      d="M274.5 0C286.19 0 295.884 11.2597 297.694 26.0059C300.642 26.1362 303 29.2168 303 33C303 36.866 300.538 40 297.5 40C297.334 40 297.171 39.9889 297.009 39.9707C295.19 48.0394 290.992 54.8338 285.5 58.7881V57.5352C290.467 53.7054 294.315 47.3741 296.033 39.751L296.23 38.877L297.121 38.9766C297.253 38.9914 297.379 39 297.5 39C299.774 39 302 36.5518 302 33C302 29.5317 299.868 27.1031 297.65 27.0049L296.805 26.9678L296.702 26.1279C294.925 11.6505 285.481 1 274.5 1C263.519 1 254.074 11.6505 252.297 26.1279L252.194 26.9678L251.349 27.0049C249.132 27.1037 247 29.5322 247 33C247 36.5518 249.226 39 251.5 39C251.621 39 251.746 38.9915 251.879 38.9766L252.769 38.877L252.966 39.751C254.685 47.3745 258.533 53.7064 263.5 57.5361V58.7881C258.008 54.8339 253.809 48.0396 251.99 39.9707C251.829 39.9889 251.665 40 251.5 40C248.462 40 246 36.866 246 33C246 29.2172 248.358 26.1368 251.305 26.0059C253.115 11.2596 262.81 0 274.5 0Z"
      fill="black"
    />

    {/* Back body outline */}
    <path
      d="M263.5 68.3008L237.734 81.9414L237.672 81.9746L237.603 81.9893L228.178 83.9727L220.326 88.3887L215.923 93.7715L212 101.618V119.577L211.977 119.651L208.481 130.634L205.5 143.059V152.038L203.488 165.114L203.477 165.15L200.483 174.626L198 186.55V231.523L197.998 231.548L196 252.527V260.896L199.42 268.713L206.207 275.5H209V272.626L205.56 266.237L205.487 266.104L207.002 250.452L207.991 250.406L209.966 260.775L212.464 263.689L214.5 260.839V245.13L210.064 237.245L210 237.131V231.432L210.019 231.365L213.519 218.865L216.013 209.884L218.006 200.42L219 190.479V176.062L218.015 172.121L217.955 171.884L218.106 171.691L223.55 164.763L229.5 145.425V136.967L229.504 136.934L230.5 129.463V119.5L231.495 119.43L233.99 136.897L237.983 151.871L237.985 151.879L237.987 151.888L240.987 164.888L240.99 164.899L242.992 175.91L243 175.955V190.532L242.996 190.564L240.996 206.064L240.991 206.102L240.98 206.138L236.987 220.113L234.992 231.586L234.991 231.594L232.991 242.094L231.5 250.542V283.963L233.494 298.921L235.494 311.421L235.5 311.46V330.54L235.494 330.579L233.494 343.079L233.492 343.089L233.49 343.098L230.99 355.598L229.5 364.541V380.965L230.994 390.926L230.996 390.936L232.494 402.424L234.491 412.906L234.494 412.921L234.496 412.936L235.996 424.436L236 424.468V446.557L235.987 446.612L234.487 453.112L234.468 453.196L234.422 453.269L230.922 458.769L230.909 458.788L230.895 458.807L227.411 463.284L224.05 468.567L225.376 472.548L232.036 473.5H248.362L253 470.717V455.59L251.532 451.676L251.479 451.534L251.513 451.388L252.979 445.033L251.532 441.176L251.5 441.091V416.956L251.508 416.913L253.008 408.413L253.012 408.388L253.02 408.362L256.013 397.886L257.511 390.896L259.5 379.459V346.891L259.546 346.79L262.507 340.373L263.5 325.979V314.938L263.515 314.879L266.015 304.879L266.018 304.868L269.007 293.905L270.5 279.974V248H278.5V279.974L279.992 293.905L282.982 304.868L282.985 304.879L285.485 314.879L285.5 314.938V325.979L286.492 340.373L289.454 346.79L289.5 346.891V379.459L291.489 390.896L292.986 397.886L295.98 408.362L295.988 408.388L295.992 408.413L297.492 416.913L297.5 416.956V441.091L297.468 441.176L296.021 445.033L297.487 451.388L297.521 451.534L297.468 451.676L296 455.59V470.717L300.638 473.5H316.964L323.623 472.548L324.949 468.567L321.588 463.284L318.105 458.807L318.091 458.788L318.078 458.769L314.578 453.269L314.532 453.196L314.513 453.112L313.013 446.612L313 446.557V424.468L313.004 424.436L314.504 412.936L314.506 412.921L314.509 412.906L316.505 402.424L318.004 390.936L318.006 390.926L319.5 380.965V364.541L318.01 355.598L315.51 343.098L315.508 343.089L315.506 343.079L313.506 330.579L313.5 330.54V311.46L313.506 311.421L315.506 298.921L317.5 283.963V250.542L316.009 242.094L314.009 231.594L314.008 231.586L312.012 220.113L308.02 206.138L308.009 206.102L308.004 206.064L306.004 190.564L306 190.532V175.955L306.008 175.91L308.01 164.899L308.013 164.888L311.013 151.888L311.015 151.879L311.017 151.871L315.009 136.897L317.505 119.43L318.5 119.5V129.463L319.496 136.934L319.5 136.967V145.425L325.449 164.763L330.894 171.691L331.045 171.884L330.985 172.121L330 176.062V190.479L330.993 200.42L332.986 209.884L335.481 218.865L338.981 231.365L339 231.432V237.131L338.936 237.245L334.5 245.13V260.839L336.536 263.689L339.033 260.775L341.009 250.406L341.998 250.452L343.513 266.104L343.44 266.237L340 272.626V275.5H342.793L349.579 268.713L353 260.896V252.527L351.002 231.548L351 231.523V186.55L348.516 174.626L345.523 165.15L345.512 165.114L343.5 152.038V143.059L340.518 130.634L337.023 119.651L337 119.577V101.618L333.076 93.7715L328.673 88.3887L320.821 83.9727L311.397 81.9893L311.328 81.9746L311.266 81.9414L285.5 68.3008V58H286.5V67.6992L311.671 81.0244L321.103 83.0107L321.178 83.0264L321.245 83.0645L329.245 87.5645L329.327 87.6104L329.387 87.6836L333.922 93.2266L333.947 93.2764L338 101.382V119.422L341.477 130.349L341.482 130.365L341.486 130.384L344.486 142.884L344.5 142.94V151.962L346.488 164.887L349.477 174.35L349.484 174.373L349.489 174.398L351.989 186.398L352 186.448V231.473L353.998 252.452L354 252.477V261.104L353.958 261.2L350.458 269.2L350.42 269.287L343.207 276.5H339V272.374L339.06 272.263L342.487 265.896L341.338 254.021L339.991 261.094L339.966 261.225L339.88 261.325L336.464 265.311L333.593 261.291L333.5 261.16V244.869L333.564 244.755L338 236.869V231.568L334.519 219.135V219.134L332.019 210.134L332.014 210.118L332.011 210.103L330.011 200.603L330.005 200.576L330.003 200.55L329.003 190.55L329 190.525V175.938L329.015 175.879L329.955 172.116L324.606 165.309L324.55 165.236L318.522 145.647L318.5 145.575V137.037L317.504 129.566L317.5 129.533V126.536L315.995 137.07L315.991 137.101L315.983 137.129L311.983 152.129L308.992 165.09L307 176.047V190.466L308.991 205.901L312.98 219.862L312.988 219.888L312.992 219.914L314.991 231.406L316.991 241.906L316.992 241.913L318.492 250.413L318.5 250.456V284.033L318.496 284.066L316.496 299.066L316.494 299.079L314.5 311.542V330.457L316.494 342.921L318.99 355.402L318.992 355.41L318.993 355.418L320.5 364.459V381.037L320.494 381.074L318.995 391.063L318.996 391.064L317.496 402.564L317.494 402.579L317.491 402.594L315.494 413.075L314 424.534V446.442L315.468 452.804L318.911 458.215L322.395 462.693L322.409 462.712L322.422 462.731L325.922 468.231L326.05 468.433L324.377 473.451L324.07 473.495L317.07 474.495L317.035 474.5H300.361L300.243 474.429L295.243 471.429L295 471.283V455.409L295.032 455.324L296.479 451.466L295.013 445.112L294.979 444.966L295.032 444.824L296.5 440.91V417.042L295.012 408.612L292.02 398.138L292.015 398.121L292.011 398.104L290.511 391.104L290.509 391.096L290.508 391.086L288.508 379.586L288.5 379.543V347.11L285.546 340.71L285.508 340.626L285.501 340.534L284.501 326.034L284.5 326.018V315.062L282.018 305.132L279.018 294.132L279.007 294.093L279.003 294.054L277.5 280.026V249H271.5V280.026L269.997 294.054L269.993 294.093L269.982 294.132L266.982 305.132L264.5 315.062V326.018L264.499 326.034L263.499 340.534L263.492 340.626L263.454 340.71L260.5 347.11V379.543L260.492 379.586L258.492 391.086L258.491 391.096L258.489 391.104L256.989 398.104L256.985 398.121L256.98 398.138L253.987 408.612L252.5 417.042V440.91L253.968 444.824L254.021 444.966L253.987 445.112L252.521 451.466L253.968 455.324L254 455.409V471.283L253.757 471.429L248.757 474.429L248.639 474.5H231.965L231.93 474.495L224.93 473.495L224.623 473.451L222.95 468.433L223.078 468.231L226.578 462.731L226.591 462.712L226.605 462.693L230.088 458.215L233.531 452.804L235 446.442V424.534L233.505 413.075L231.509 402.594L231.506 402.579L231.504 402.564L230.004 391.064V391.063L228.506 381.074L228.5 381.037V364.459L230.007 355.418L230.008 355.41L230.01 355.402L232.506 342.921L234.5 330.457V311.542L232.506 299.079L232.504 299.066L230.504 284.066L230.5 284.033V250.456L230.508 250.413L232.008 241.913L232.009 241.906L234.009 231.406L236.008 219.914L236.012 219.888L236.02 219.862L240.008 205.901L242 190.466V176.047L240.008 165.09H240.007L237.017 152.129L233.017 137.129L233.009 137.101L233.005 137.07L231.5 126.536V129.533L231.496 129.566L230.5 137.037V145.575L230.478 145.647L224.45 165.236L224.394 165.309L219.044 172.116L219.985 175.879L220 175.938V190.525L219.997 190.55L218.997 200.55L218.995 200.576L218.989 200.603L216.989 210.103L216.986 210.118L216.981 210.134L214.481 219.134V219.135L211 231.568V236.869L215.436 244.755L215.5 244.869V261.16L215.407 261.291L212.536 265.311L209.12 261.325L209.034 261.225L209.009 261.094L207.661 254.021L206.512 265.896L209.94 272.263L210 272.374V276.5H205.793L198.58 269.287L198.542 269.2L195.042 261.2L195 261.104V252.477L195.002 252.452L197 231.473V186.448L197.011 186.398L199.511 174.398L199.516 174.373L199.523 174.35L202.511 164.887L204.5 151.962V142.94L204.514 142.884L207.514 130.384L207.518 130.365L207.523 130.349L211 119.422V101.382L215.053 93.2764L215.078 93.2266L219.613 87.6836L219.673 87.6104L219.755 87.5645L227.755 83.0645L227.822 83.0264L227.897 83.0107L237.328 81.0244L262.5 67.6992V58H263.5V68.3008Z"
      fill="black"
    />

    {/* Back body center line */}
    <path d="M275 117V201" stroke="black" />
  </g>
);

// Alias components for front and back - they render the same combined SVG
// The viewBox in renderBodyView will clip to show only the relevant portion
const BodyOutlineFront = BodyOutline;
const BodyOutlineBack = BodyOutline;

// Map of named body regions to representative (x, y) centers. Used when a
// parent passes `selectedParts` as strings (e.g. when displaying a saved
// injury report) so we can reverse-render the marker dots on the diagram.
// Coordinates are the midpoints of the bounding boxes in getBodyPartNameFront
// / getBodyPartNameBack above.
// Screen-left (small x) = "Left ..." to match the non-mirrored front labelling above.
// Coordinates are the midpoints of the detection bands in getBodyPartNameFront.
const FRONT_PART_CENTERS = {
  Head: { x: 80, y: 31 },
  'Left Ear': { x: 54, y: 37 },
  'Right Ear': { x: 106, y: 37 },
  Neck: { x: 80, y: 72 },
  'Left Shoulder': { x: 34, y: 98 },
  'Right Shoulder': { x: 126, y: 98 },
  'Left Upper Arm': { x: 20, y: 135 },
  'Right Upper Arm': { x: 140, y: 135 },
  'Left Elbow': { x: 22, y: 178 },
  'Right Elbow': { x: 138, y: 178 },
  'Left Forearm': { x: 18, y: 216 },
  'Right Forearm': { x: 142, y: 216 },
  'Left Hand': { x: 14, y: 272 },
  'Right Hand': { x: 146, y: 272 },
  Chest: { x: 80, y: 128 },
  Abdomen: { x: 80, y: 205 },
  'Left Hip': { x: 48, y: 268 },
  'Right Hip': { x: 112, y: 268 },
  Groin: { x: 80, y: 273 },
  'Left Thigh': { x: 57, y: 318 },
  'Right Thigh': { x: 103, y: 318 },
  'Left Knee': { x: 58, y: 368 },
  'Right Knee': { x: 102, y: 368 },
  'Left Shin': { x: 58, y: 425 },
  'Right Shin': { x: 102, y: 425 },
  'Left Foot': { x: 50, y: 467 },
  'Right Foot': { x: 110, y: 467 },
  'Front Body': { x: 80, y: 205 }
};

// Coordinates are the midpoints of the detection bands in getBodyPartNameBack.
const BACK_PART_CENTERS = {
  'Back of Head': { x: 80, y: 31 },
  'Left Ear': { x: 54, y: 37 },
  'Right Ear': { x: 106, y: 37 },
  'Back of Neck': { x: 80, y: 72 },
  'Left Shoulder': { x: 34, y: 98 },
  'Right Shoulder': { x: 126, y: 98 },
  'Left Back Upper Arm': { x: 20, y: 135 },
  'Right Back Upper Arm': { x: 140, y: 135 },
  'Left Back Elbow': { x: 22, y: 178 },
  'Right Back Elbow': { x: 138, y: 178 },
  'Left Back Forearm': { x: 18, y: 216 },
  'Right Back Forearm': { x: 142, y: 216 },
  'Left Back Hand': { x: 14, y: 272 },
  'Right Back Hand': { x: 146, y: 272 },
  'Upper Back': { x: 80, y: 128 },
  'Lower Back': { x: 80, y: 205 },
  'Left Back Hip': { x: 48, y: 268 },
  'Right Back Hip': { x: 112, y: 268 },
  Buttocks: { x: 80, y: 273 },
  'Left Back Thigh': { x: 57, y: 318 },
  'Right Back Thigh': { x: 103, y: 318 },
  'Left Back Knee': { x: 58, y: 368 },
  'Right Back Knee': { x: 102, y: 368 },
  'Left Calf': { x: 58, y: 425 },
  'Right Calf': { x: 102, y: 425 },
  'Left Back Foot': { x: 50, y: 467 },
  'Right Back Foot': { x: 110, y: 467 },
  'Back Body': { x: 80, y: 205 }
};

const BodyDiagram = ({ selectedParts = [], onChange, onDescriptionAdd, disabled = false }) => {
  const theme = useTheme();
  const intl = useIntl();

  // Helper function to translate body part names
  const getBodyPartLabel = (bodyPartName) => {
    const translationKey = BODY_PART_TRANSLATION_KEYS[bodyPartName];
    if (translationKey) {
      return intl.formatMessage({ id: translationKey, defaultMessage: bodyPartName });
    }
    return bodyPartName;
  };

  const [frontMarkers, setFrontMarkers] = useState([]);
  const [backMarkers, setBackMarkers] = useState([]);
  // In editable mode we seed the initial markers exactly ONCE (see below); this
  // guards against re-seeding — which would wipe the user's in-progress edits —
  // on every parent re-render.
  const didHydrateEditableRef = useRef(false);

  // Hydrate markers from `selectedParts` (an array of body-part names). Saved injury
  // reports store names only, no coordinates, so we reverse-map each name to a
  // representative coordinate from FRONT_PART_CENTERS / BACK_PART_CENTERS and seed
  // markers. Without this, passing `selectedParts` produced no markers.
  //
  // READ-ONLY (display) mode re-syncs whenever the list changes. EDITABLE mode seeds
  // just once on mount, so a supervisor correcting an existing report starts from its
  // recorded markers yet can freely add/move/remove them afterwards without the seed
  // fighting their edits. A brand-new therapist form passes an empty array and is
  // unaffected (the length-0 guard returns first).
  useEffect(() => {
    if (!Array.isArray(selectedParts) || selectedParts.length === 0) return;
    if (!disabled) {
      if (didHydrateEditableRef.current) return;
      didHydrateEditableRef.current = true;
    }

    const front = [];
    const back = [];
    selectedParts.forEach((name, idx) => {
      if (FRONT_PART_CENTERS[name]) {
        front.push({ id: `hydrated-front-${idx}`, ...FRONT_PART_CENTERS[name], bodyPart: name, view: 'front' });
      } else if (BACK_PART_CENTERS[name]) {
        back.push({ id: `hydrated-back-${idx}`, ...BACK_PART_CENTERS[name], bodyPart: name, view: 'back' });
      }
    });
    setFrontMarkers(front);
    setBackMarkers(back);
    // selectedParts comes from a parent that may rebuild the array each render;
    // we don't want to keep resetting markers after the initial hydration if the
    // user is interacting — so we re-run only when the *length* or `disabled` flag
    // changes. (For read-only mode the array is effectively static.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParts.length, disabled]);
  const [draggingMarker, setDraggingMarker] = useState(null);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverBodyPart, setPopoverBodyPart] = useState('');
  const [popoverDescription, setPopoverDescription] = useState('');
  const [popoverMarkerId, setPopoverMarkerId] = useState(null);
  const [popoverMarkerView, setPopoverMarkerView] = useState(null);
  const [popoverFlip, setPopoverFlip] = useState(false);
  const frontSvgRef = useRef(null);
  const backSvgRef = useRef(null);
  const anchorElRef = useRef(null);
  const popoverOpenRef = useRef(false);

  // Close popover on scroll — standard UX for anchored popovers
  const handleScrollClose = useCallback(() => {
    if (popoverOpenRef.current) {
      // Apply any edited body part name before closing
      if (anchorElRef.current) {
        anchorElRef.current.remove();
        anchorElRef.current = null;
      }
      popoverOpenRef.current = false;
      setPopoverAnchor(null);
      setPopoverBodyPart('');
      setPopoverDescription('');
      setPopoverMarkerId(null);
      setPopoverMarkerView(null);
    }
  }, []);

  useEffect(() => {
    if (popoverAnchor) {
      popoverOpenRef.current = true;
      // Listen on the nearest scrollable ancestor (capture phase catches all scroll containers)
      window.addEventListener('scroll', handleScrollClose, true);
      return () => window.removeEventListener('scroll', handleScrollClose, true);
    }
    popoverOpenRef.current = false;
  }, [popoverAnchor, handleScrollClose]);

  // Get SVG coordinates from mouse event - for front view (0-160 width, 0-475 height)
  const getSvgCoordinatesFront = (event, svgRef) => {
    const svg = svgRef.current;
    if (!svg) return null;

    const rect = svg.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 160;
    const y = ((event.clientY - rect.top) / rect.height) * SVG_HEIGHT;

    return { x, y };
  };

  // Get SVG coordinates from mouse event - for back view (offset by 195 in the original SVG)
  const getSvgCoordinatesBack = (event, svgRef) => {
    const svg = svgRef.current;
    if (!svg) return null;

    const rect = svg.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 160;
    const y = ((event.clientY - rect.top) / rect.height) * SVG_HEIGHT;

    return { x, y };
  };

  // Check if click is on an existing marker
  const isClickOnMarker = (clickX, clickY, markers, markerRadius = 16) => {
    return markers.some((marker) => {
      const dx = clickX - marker.x;
      const dy = clickY - marker.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      return distance <= markerRadius;
    });
  };

  // Show description popover near the click
  const openDescriptionPopover = (event, bodyPart, markerId, markerView) => {
    if (!onDescriptionAdd) return;
    const clickY = event.clientY;
    const viewportHeight = window.innerHeight;
    // Flip upward if click is in the lower 40% of the viewport
    const shouldFlip = clickY > viewportHeight * 0.6;
    setPopoverFlip(shouldFlip);

    // Create a real DOM anchor element inside the SVG's parent so it scrolls with content
    const svgRef = markerView === 'front' ? frontSvgRef : backSvgRef;
    const svgEl = svgRef.current;
    if (svgEl) {
      // Remove previous anchor if any
      if (anchorElRef.current) anchorElRef.current.remove();
      const parent = svgEl.parentElement;
      const parentRect = parent.getBoundingClientRect();
      const anchor = document.createElement('span');
      anchor.style.position = 'absolute';
      anchor.style.left = `${event.clientX - parentRect.left}px`;
      anchor.style.top = `${event.clientY - parentRect.top}px`;
      anchor.style.width = '0';
      anchor.style.height = '0';
      anchor.style.pointerEvents = 'none';
      // Ensure parent has relative positioning for absolute child
      if (getComputedStyle(parent).position === 'static') {
        parent.style.position = 'relative';
      }
      parent.appendChild(anchor);
      anchorElRef.current = anchor;
      setPopoverAnchor(anchor);
    }

    setPopoverBodyPart(bodyPart);
    setPopoverDescription('');
    setPopoverMarkerId(markerId);
    setPopoverMarkerView(markerView);
  };

  // Update the marker's bodyPart label and notify parent
  const applyBodyPartEdit = (editedName) => {
    if (!popoverMarkerId || !editedName) return;
    if (popoverMarkerView === 'front') {
      const updated = frontMarkers.map((m) => (m.id === popoverMarkerId ? { ...m, bodyPart: editedName } : m));
      setFrontMarkers(updated);
      updateParent(updated, backMarkers);
    } else {
      const updated = backMarkers.map((m) => (m.id === popoverMarkerId ? { ...m, bodyPart: editedName } : m));
      setBackMarkers(updated);
      updateParent(frontMarkers, updated);
    }
  };

  const cleanupPopover = () => {
    if (anchorElRef.current) {
      anchorElRef.current.remove();
      anchorElRef.current = null;
    }
    setPopoverAnchor(null);
    setPopoverBodyPart('');
    setPopoverDescription('');
    setPopoverMarkerId(null);
    setPopoverMarkerView(null);
  };

  const handlePopoverSave = () => {
    const editedName = popoverBodyPart.trim();
    if (editedName) applyBodyPartEdit(editedName);
    if (popoverDescription.trim() && onDescriptionAdd) {
      onDescriptionAdd(editedName || popoverBodyPart, popoverDescription.trim());
    }
    cleanupPopover();
  };

  const handlePopoverClose = () => {
    // Still apply edited body part name on skip
    const editedName = popoverBodyPart.trim();
    if (editedName) applyBodyPartEdit(editedName);
    cleanupPopover();
  };

  // Handle click on front body to add marker
  const handleFrontClick = (event) => {
    if (disabled || draggingMarker) return;

    const coords = getSvgCoordinatesFront(event, frontSvgRef);
    if (!coords) return;

    // Don't add a new marker if clicking on an existing one
    if (isClickOnMarker(coords.x, coords.y, frontMarkers)) return;

    const bodyPart = getBodyPartNameFront(coords.x, coords.y);
    const newMarker = {
      id: `front-${Date.now()}`,
      x: coords.x,
      y: coords.y,
      view: 'front',
      bodyPart
    };

    const newFrontMarkers = [...frontMarkers, newMarker];
    setFrontMarkers(newFrontMarkers);
    updateParent(newFrontMarkers, backMarkers);
    openDescriptionPopover(event, bodyPart, newMarker.id, 'front');
  };

  // Handle click on back body to add marker
  const handleBackClick = (event) => {
    if (disabled || draggingMarker) return;

    const coords = getSvgCoordinatesBack(event, backSvgRef);
    if (!coords) return;

    // Don't add a new marker if clicking on an existing one
    if (isClickOnMarker(coords.x, coords.y, backMarkers)) return;

    const bodyPart = getBodyPartNameBack(coords.x, coords.y);
    const newMarker = {
      id: `back-${Date.now()}`,
      x: coords.x,
      y: coords.y,
      view: 'back',
      bodyPart
    };

    const newBackMarkers = [...backMarkers, newMarker];
    setBackMarkers(newBackMarkers);
    updateParent(frontMarkers, newBackMarkers);
    openDescriptionPopover(event, bodyPart, newMarker.id, 'back');
  };

  // Start dragging a marker
  const handleMarkerMouseDown = (event, marker, view) => {
    if (disabled) return;
    event.stopPropagation();
    setDraggingMarker({ ...marker, view });
  };

  // Handle mouse move for dragging
  const handleMouseMove = (event, svgRef, view) => {
    if (!draggingMarker || draggingMarker.view !== view) return;

    const coords = view === 'front' ? getSvgCoordinatesFront(event, svgRef) : getSvgCoordinatesBack(event, svgRef);
    if (!coords) return;

    const newBodyPart = view === 'front' ? getBodyPartNameFront(coords.x, coords.y) : getBodyPartNameBack(coords.x, coords.y);

    if (view === 'front') {
      const updated = frontMarkers.map((m) => (m.id === draggingMarker.id ? { ...m, x: coords.x, y: coords.y, bodyPart: newBodyPart } : m));
      setFrontMarkers(updated);
    } else {
      const updated = backMarkers.map((m) => (m.id === draggingMarker.id ? { ...m, x: coords.x, y: coords.y, bodyPart: newBodyPart } : m));
      setBackMarkers(updated);
    }
  };

  // Stop dragging
  const handleMouseUp = (event, svgRef, view) => {
    if (draggingMarker && draggingMarker.view === view) {
      const coords = view === 'front' ? getSvgCoordinatesFront(event, svgRef) : getSvgCoordinatesBack(event, svgRef);

      // Check if dropped outside the body area (with some padding)
      const isOutside = !coords || coords.x < 0 || coords.x > 160 || coords.y < 0 || coords.y > SVG_HEIGHT;

      if (isOutside) {
        // Remove the marker
        if (view === 'front') {
          const updated = frontMarkers.filter((m) => m.id !== draggingMarker.id);
          setFrontMarkers(updated);
          updateParent(updated, backMarkers);
        } else {
          const updated = backMarkers.filter((m) => m.id !== draggingMarker.id);
          setBackMarkers(updated);
          updateParent(frontMarkers, updated);
        }
      } else {
        updateParent(frontMarkers, backMarkers);
      }
    }
    setDraggingMarker(null);
  };

  // Handle mouse leave - remove marker if dragged outside
  const handleMouseLeave = (view) => {
    if (draggingMarker && draggingMarker.view === view) {
      // Remove the marker when dragged outside
      if (view === 'front') {
        const updated = frontMarkers.filter((m) => m.id !== draggingMarker.id);
        setFrontMarkers(updated);
        updateParent(updated, backMarkers);
      } else {
        const updated = backMarkers.filter((m) => m.id !== draggingMarker.id);
        setBackMarkers(updated);
        updateParent(frontMarkers, updated);
      }
      setDraggingMarker(null);
    }
  };

  // Remove a marker
  const handleRemoveMarker = (markerId, view) => {
    if (disabled) return;

    if (view === 'front') {
      const updated = frontMarkers.filter((m) => m.id !== markerId);
      setFrontMarkers(updated);
      updateParent(updated, backMarkers);
    } else {
      const updated = backMarkers.filter((m) => m.id !== markerId);
      setBackMarkers(updated);
      updateParent(frontMarkers, updated);
    }
  };

  // Update parent component
  const updateParent = (front, back) => {
    const allMarkers = [...front.map((m) => m.bodyPart || 'Front Body'), ...back.map((m) => m.bodyPart || 'Back Body')];
    onChange && onChange(allMarkers);
  };

  // Clear all markers
  const handleClearAll = () => {
    if (disabled) return;
    setFrontMarkers([]);
    setBackMarkers([]);
    onChange && onChange([]);
  };

  // Render markers
  const renderMarkers = (markers, view) => {
    // Back view markers need x offset of 195 to match the SVG coordinate system
    const xOffset = view === 'back' ? 195 : 0;

    return (
      <>
        {markers.map((marker, index) => (
          <g key={marker.id}>
            {/* Outer glow */}
            <circle
              cx={marker.x + xOffset}
              cy={marker.y}
              r={16}
              fill="none"
              stroke={theme.palette.error.main}
              strokeWidth="2"
              opacity="0.3"
            />
            {/* Main marker */}
            <circle
              cx={marker.x + xOffset}
              cy={marker.y}
              r={12}
              fill={alpha(theme.palette.error.main, 0.7)}
              stroke={theme.palette.error.main}
              strokeWidth="2"
              style={{ cursor: disabled ? 'default' : 'move' }}
              onMouseDown={(e) => handleMarkerMouseDown(e, marker, view)}
            />
            {/* Number label */}
            <text
              x={marker.x + xOffset}
              y={marker.y + 4}
              textAnchor="middle"
              fill="#fff"
              fontSize="11"
              fontWeight="bold"
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              {index + 1}
            </text>
          </g>
        ))}
      </>
    );
  };

  const renderBodyView = (OutlineComponent, label, markers, svgRef, onClick, view) => {
    // Front view uses viewBox 0 0 160 475, back view uses 195 0 160 475 (offset)
    const viewBox = view === 'front' ? '0 0 160 475' : '195 0 160 475';

    return (
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          p: 2,
          bgcolor: alpha(theme.palette.grey[100], 0.5),
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minWidth: 140
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, letterSpacing: 1 }}>
          {label}
        </Typography>
        <svg
          ref={svgRef}
          viewBox={viewBox}
          style={{
            width: '100%',
            maxWidth: '160px',
            height: 'auto',
            cursor: disabled ? 'default' : 'crosshair'
          }}
          onClick={onClick}
          onMouseMove={(e) => handleMouseMove(e, svgRef, view)}
          onMouseUp={(e) => handleMouseUp(e, svgRef, view)}
          onMouseLeave={() => handleMouseLeave(view)}
        >
          <OutlineComponent />
          {renderMarkers(markers, view)}
        </svg>
      </Paper>
    );
  };

  const totalMarkers = frontMarkers.length + backMarkers.length;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {intl.formatMessage({ id: 'childReport.injury.section.locationOnBody' })}
        </Typography>
        {totalMarkers > 0 && !disabled && (
          <IconButton size="small" color="error" onClick={handleClearAll}>
            <DeleteOutlined />
          </IconButton>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        {intl.formatMessage({ id: 'childReport.injury.bodyDiagramInstruction' })}
      </Typography>

      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 2,
          bgcolor: 'background.paper'
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="stretch">
          {renderBodyView(BodyOutlineFront, 'FRONT', frontMarkers, frontSvgRef, handleFrontClick, 'front')}
          {renderBodyView(BodyOutlineBack, 'BACK', backMarkers, backSvgRef, handleBackClick, 'back')}
        </Stack>

        {/* Markers count */}
        {totalMarkers > 0 && (
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: `1px solid ${theme.palette.divider}`
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'error.main'
                }}
              />
              <Typography variant="subtitle2" color="text.secondary">
                {totalMarkers} injury location{totalMarkers > 1 ? 's' : ''} marked
                {frontMarkers.length > 0 && ` (${frontMarkers.length} front`}
                {frontMarkers.length > 0 && backMarkers.length > 0 && ', '}
                {backMarkers.length > 0 && `${backMarkers.length} back`}
                {(frontMarkers.length > 0 || backMarkers.length > 0) && ')'}
              </Typography>
            </Stack>
          </Box>
        )}

        {totalMarkers === 0 && (
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No injury locations marked
            </Typography>
          </Box>
        )}

        {/* Body parts chips */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {intl.formatMessage({ id: 'form.bodyDiagram.injuredBodyParts', defaultMessage: 'Injured Body Parts' })}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              p: 1.5,
              minHeight: 48,
              bgcolor: alpha(theme.palette.grey[100], 0.5),
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            {frontMarkers.map((m, i) => (
              <Chip
                key={m.id}
                label={`${i + 1}. ${getBodyPartLabel(m.bodyPart || 'Front Body')}`}
                size="small"
                color="error"
                variant="outlined"
                onDelete={disabled ? undefined : () => handleRemoveMarker(m.id, 'front')}
              />
            ))}
            {backMarkers.map((m, i) => (
              <Chip
                key={m.id}
                label={`${frontMarkers.length + i + 1}. ${getBodyPartLabel(m.bodyPart || 'Back Body')}`}
                size="small"
                color="error"
                variant="outlined"
                onDelete={disabled ? undefined : () => handleRemoveMarker(m.id, 'back')}
              />
            ))}
            {totalMarkers === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                {intl.formatMessage({
                  id: 'form.bodyDiagram.clickToMarkInstructions',
                  defaultMessage: 'Click on the body diagram to mark injury locations'
                })}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Description popover - appears at click location */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: popoverFlip ? 'top' : 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: popoverFlip ? 'bottom' : 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              width: 380,
              borderRadius: 3,
              border: `2px solid ${theme.palette.error.main}`,
              boxShadow: `0 8px 32px ${alpha(theme.palette.error.main, 0.18)}`,
              overflow: 'visible',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: '50%',
                width: 14,
                height: 14,
                bgcolor: 'background.paper',
                ...(popoverFlip
                  ? {
                      bottom: -8,
                      transform: 'translateX(-50%) rotate(225deg)',
                      borderLeft: `2px solid ${theme.palette.error.main}`,
                      borderTop: `2px solid ${theme.palette.error.main}`
                    }
                  : {
                      top: -8,
                      transform: 'translateX(-50%) rotate(45deg)',
                      borderLeft: `2px solid ${theme.palette.error.main}`,
                      borderTop: `2px solid ${theme.palette.error.main}`
                    })
              }
            }
          }
        }}
      >
        <Box
          sx={{
            px: 2.5,
            pt: 2,
            pb: 1.5,
            bgcolor: alpha(theme.palette.error.main, 0.06),
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Injury Location (edit to be more specific)
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={popoverBodyPart}
            onChange={(e) => setPopoverBodyPart(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontWeight: 700,
                fontSize: '0.95rem',
                bgcolor: 'background.paper',
                borderRadius: 1.5
              }
            }}
          />
        </Box>
        <Box sx={{ p: 2.5 }}>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            size="small"
            placeholder="E.g. Bruise on the left side, redness and swelling observed..."
            value={popoverDescription}
            onChange={(e) => setPopoverDescription(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handlePopoverSave();
              }
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.875rem'
              }
            }}
          />
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button size="small" onClick={handlePopoverClose} sx={{ textTransform: 'none', px: 2 }}>
              Skip
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={handlePopoverSave}
              disabled={!popoverDescription.trim()}
              sx={{ textTransform: 'none', px: 3, borderRadius: 2 }}
            >
              Add Description
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

BodyDiagram.propTypes = {
  selectedParts: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  onDescriptionAdd: PropTypes.func,
  disabled: PropTypes.bool
};

export default BodyDiagram;
